import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CalendarRange, Database, Download, LucideAngularModule, RefreshCcw } from 'lucide-angular';
import { ForecastPriceSnapshot, ForecastPriceSnapshotDetail } from '../../../core/models/domain.models';
import { ToastService } from '../../../shared/ui/toast.service';
import { BessOperationsService } from '../services/bess-operations.service';

type ForecastChartLine = {
  label: string;
  color: string;
  fill: string;
  points: string;
  areaPath: string;
  currentPoint: string;
  latestValue: number | null;
  minValue: number | null;
  maxValue: number | null;
};

type ForecastTooltipBlock = {
  blockNumber: number;
  blockLabel: string;
  centerX: number;
  leftX: number;
  width: number;
  marketPrice: string | number | null | undefined;
  sourcePrice: string | number | null | undefined;
  mcpPrice: string | number | null | undefined;
};

@Component({
  standalone: true,
  selector: 'app-forecast-market-price-page',
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './forecast-market-price.page.html',
  styleUrl: './forecast-market-price.page.scss',
})
export class ForecastMarketPricePage implements OnInit {
  private readonly service = inject(BessOperationsService);
  private readonly toast = inject(ToastService);

  protected readonly productOptions = ['DAM', 'GDAM', 'RTM'] as const;
  protected readonly CalendarRangeIcon = CalendarRange;
  protected readonly DatabaseIcon = Database;
  protected readonly DownloadIcon = Download;
  protected readonly RefreshCcwIcon = RefreshCcw;

  protected readonly loading = signal(false);
  protected readonly fetching = signal(false);
  protected readonly fetchingAll = signal(false);
  protected readonly refetchingById = signal<Record<string, boolean>>({});
  protected readonly snapshots = signal<ForecastPriceSnapshot[]>([]);
  protected readonly selectedSnapshot = signal<ForecastPriceSnapshotDetail | null>(null);
  protected readonly activeSnapshotId = signal<string | null>(null);
  protected readonly form = signal({
    product: 'RTM',
    forecast_date: this.todayValue(),
  });
  protected readonly selectedProduct = computed(() => this.form().product as 'DAM' | 'GDAM' | 'RTM');
  protected readonly forecastHourBlocks = [1, 25, 49, 73, 96];
  protected readonly currentBlock = computed(() => this.resolveCurrentBlock());
  protected readonly currentBlockLabel = computed(() => this.blockStartLabel(this.currentBlock()));
  protected readonly currentBlockX = computed(() => this.chartX(this.currentBlock()));
  protected readonly hoveredBlock = signal<number | null>(null);
  protected readonly chartBlocks = computed<ForecastTooltipBlock[]>(() => {
    const blocks = this.selectedSnapshot()?.blocks || [];
    return blocks.map((block, index, allBlocks) => {
      const centerX = this.chartX(block.block_number);
      const previousCenter = index > 0 ? this.chartX(allBlocks[index - 1].block_number) : 24;
      const nextCenter = index < allBlocks.length - 1 ? this.chartX(allBlocks[index + 1].block_number) : 936;
      const leftX = index === 0 ? 24 : (previousCenter + centerX) / 2;
      const rightX = index === allBlocks.length - 1 ? 936 : (centerX + nextCenter) / 2;
      return {
        blockNumber: block.block_number,
        blockLabel: block.block_label,
        centerX,
        leftX,
        width: Math.max(6, rightX - leftX),
        marketPrice: block.market_price,
        sourcePrice: block.source_price,
        mcpPrice: block.mcp_price,
      };
    });
  });
  protected readonly hoveredChartBlock = computed(() => {
    const hoveredBlock = this.hoveredBlock();
    if (hoveredBlock === null) return null;
    return this.chartBlocks().find((block) => block.blockNumber === hoveredBlock) || null;
  });
  protected readonly hoveredBlockX = computed(() => this.hoveredChartBlock()?.centerX ?? null);
  protected readonly hoveredTooltipLeft = computed(() => {
    const centerX = this.hoveredBlockX();
    if (centerX === null) return null;
    return Math.max(0, Math.min(100, ((centerX - 24) / (936 - 24)) * 100));
  });

  protected readonly groupedBlocks = computed(() => {
    const blocks = this.selectedSnapshot()?.blocks || [];
    const groups: Array<{ hourLabel: string; items: ForecastPriceSnapshotDetail['blocks'] }> = [];
    for (let index = 0; index < blocks.length; index += 4) {
      const startBlock = blocks[index];
      groups.push({
        hourLabel: startBlock?.block_label?.split('-')[0]?.slice(0, 5) || `Hour ${Math.floor(index / 4) + 1}`,
        items: blocks.slice(index, index + 4),
      });
    }
    return groups;
  });
  protected readonly chartLines = computed<ForecastChartLine[]>(() => this.buildChartLines());
  protected readonly chartBounds = computed(() => {
    const values = this.chartLines()
      .flatMap((line) => [line.minValue, line.maxValue])
      .filter((value): value is number => Number.isFinite(value));
    if (values.length === 0) {
      return { min: 0, max: 10 };
    }
    return { min: Math.min(...values), max: Math.max(...values) };
  });

  ngOnInit(): void {
    this.loadSnapshots();
  }

  protected updateField(field: 'product' | 'forecast_date', value: string): void {
    this.form.update((current) => ({ ...current, [field]: value }));
    if (field === 'product') {
      this.activeSnapshotId.set(null);
      this.selectedSnapshot.set(null);
    }
    this.loadSnapshots();
  }

  protected fetchSnapshot(forceRefetch = false): void {
    const payload = this.form();
    this.fetching.set(true);
    this.service.autoFetchForecastPrice(this.selectedProduct(), payload.forecast_date, forceRefetch).subscribe({
      next: (result) => {
        this.fetching.set(false);
        this.toast.show(
          result.created_count > 0 ? `${payload.product} forecast market price stored` : `${payload.product} forecast market price already available`,
          '',
          'success',
        );
        this.loadSnapshots();
      },
      error: (error) => {
        this.fetching.set(false);
        this.toast.show(error?.error?.detail || 'Failed to fetch forecast market price', '', 'danger');
      },
    });
  }

  protected fetchAllProducts(forceRefetch = false): void {
    const forecastDate = this.form().forecast_date;
    this.fetchingAll.set(true);
    let completed = 0;
    let failed = false;
    for (const product of this.productOptions) {
      this.service.autoFetchForecastPrice(product, forecastDate, forceRefetch).subscribe({
        next: () => {
          completed += 1;
          if (completed === this.productOptions.length) {
            this.fetchingAll.set(false);
            this.toast.show('DAM, GDAM, and RTM forecast prices stored', '', failed ? 'info' : 'success');
            this.loadSnapshots();
          }
        },
        error: () => {
          failed = true;
          completed += 1;
          if (completed === this.productOptions.length) {
            this.fetchingAll.set(false);
            this.toast.show('Some forecast market prices failed to fetch', '', 'info');
            this.loadSnapshots();
          }
        },
      });
    }
  }

  protected selectSnapshot(snapshotId: string): void {
    this.activeSnapshotId.set(snapshotId);
    this.service.getForecastPrice(snapshotId).subscribe({
      next: (detail) => this.selectedSnapshot.set(detail),
      error: () => this.toast.show('Failed to load stored blockwise data', '', 'danger'),
    });
  }

  protected refetchSnapshot(snapshot: ForecastPriceSnapshot): void {
    this.refetchingById.set({ ...this.refetchingById(), [snapshot.id]: true });
    this.service.refetchForecastPrice(snapshot.id).subscribe({
      next: (detail) => {
        this.refetchingById.set({ ...this.refetchingById(), [snapshot.id]: false });
        this.toast.show(`${snapshot.product} forecast market price refreshed`, '', 'success');
        this.selectedSnapshot.set(detail);
        this.activeSnapshotId.set(detail.id);
        this.loadSnapshots();
      },
      error: () => {
        this.refetchingById.set({ ...this.refetchingById(), [snapshot.id]: false });
        this.toast.show('Failed to refresh forecast market price', '', 'danger');
      },
    });
  }

  protected isRefetching(snapshotId: string): boolean {
    return !!this.refetchingById()[snapshotId];
  }

  protected openSource(snapshot: ForecastPriceSnapshot | null): void {
    if (!snapshot?.source_url) return;
    window.open(snapshot.source_url, '_blank', 'noopener');
  }

  protected formatPrice(value: string | number | null | undefined): string {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toFixed(4).replace(/0+$/, '').replace(/\.$/, '') : '-';
  }

  protected formatDisplayDate(value: string | null | undefined): string {
    const text = (value || '').trim();
    if (!text) return '-';

    const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
    }

    const dmyMatch = text.match(/^(\d{2})-(\d{2})-(\d{4})/);
    if (dmyMatch) {
      return `${dmyMatch[1]}-${dmyMatch[2]}-${dmyMatch[3]}`;
    }

    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) {
      return text;
    }

    const day = `${parsed.getDate()}`.padStart(2, '0');
    const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
    const year = `${parsed.getFullYear()}`;
    return `${day}-${month}-${year}`;
  }

  protected chartX(block: number): number {
    const left = 24;
    const right = 936;
    if (block <= 1) return left;
    if (block >= 96) return right;
    return left + ((block - 1) / 95) * (right - left);
  }

  protected hourLabel(block: number): string {
    return this.blockStartLabel(block);
  }

  protected showBlockTooltip(blockNumber: number): void {
    this.hoveredBlock.set(blockNumber);
  }

  protected clearBlockTooltip(): void {
    this.hoveredBlock.set(null);
  }

  private loadSnapshots(): void {
    const payload = this.form();
    this.loading.set(true);
    this.service.listForecastPrices(this.selectedProduct(), payload.forecast_date).subscribe({
      next: (rows) => {
        this.snapshots.set(rows || []);
        this.loading.set(false);
        const currentId = this.activeSnapshotId() || rows?.[0]?.id || null;
        if (currentId) {
          this.selectSnapshot(currentId);
        } else {
          this.selectedSnapshot.set(null);
        }
        this.hoveredBlock.set(null);
      },
      error: () => {
        this.loading.set(false);
        this.snapshots.set([]);
        this.selectedSnapshot.set(null);
        this.toast.show('Failed to load forecast market prices', '', 'danger');
      },
    });
  }

  private todayValue(): string {
    const now = new Date();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
  }

  private buildChartLines(): ForecastChartLine[] {
    const blocks = this.selectedSnapshot()?.blocks || [];
    if (!blocks.length) {
      return [];
    }

    const marketValues = blocks.map((block) => this.toFiniteNumber(block.market_price));
    const sourceValues = blocks.map((block) => this.toFiniteNumber(block.source_price));
    const mcpValues = blocks.map((block) => this.toFiniteNumber(block.mcp_price));
    const allValues = [...marketValues, ...sourceValues, ...mcpValues].filter((value): value is number => Number.isFinite(value));
    const min = allValues.length ? Math.min(...allValues) : 0;
    const max = allValues.length ? Math.max(...allValues) : 10;

    return [
      this.createChartLine('Market Price(Rs)', '#0f766e', 'rgba(15, 118, 110, 0.16)', marketValues, min, max),
      this.createChartLine('Source Price', '#2563eb', 'rgba(37, 99, 235, 0.12)', sourceValues, min, max),
      this.createChartLine('MCP Price', '#dc2626', 'rgba(220, 38, 38, 0.08)', mcpValues, min, max),
    ].filter((line) => line.points);
  }

  private createChartLine(label: string, color: string, fill: string, values: Array<number | null>, min: number, max: number): ForecastChartLine {
    const validValues = values.filter((value): value is number => Number.isFinite(value));
    const currentValue = values[this.currentBlock() - 1];
    return {
      label,
      color,
      fill,
      points: this.buildLinePoints(values, min, max),
      areaPath: this.buildAreaPath(values, min, max),
      currentPoint: Number.isFinite(currentValue)
        ? `${this.chartX(this.currentBlock()).toFixed(2)},${this.chartY(Number(currentValue), min, max).toFixed(2)}`
        : '',
      latestValue: Number.isFinite(currentValue) ? Number(currentValue) : null,
      minValue: validValues.length ? Math.min(...validValues) : null,
      maxValue: validValues.length ? Math.max(...validValues) : null,
    };
  }

  private buildLinePoints(values: Array<number | null>, min: number, max: number): string {
    return values
      .map((value, index) => {
        if (!Number.isFinite(value)) {
          return null;
        }
        return `${this.chartX(index + 1).toFixed(2)},${this.chartY(Number(value), min, max).toFixed(2)}`;
      })
      .filter((value): value is string => !!value)
      .join(' ');
  }

  private buildAreaPath(values: Array<number | null>, min: number, max: number): string {
    const points = values
      .map((value, index) => {
        if (!Number.isFinite(value)) {
          return null;
        }
        return { x: this.chartX(index + 1), y: this.chartY(Number(value), min, max) };
      })
      .filter((value): value is { x: number; y: number } => value !== null);

    if (points.length < 2) {
      return '';
    }

    const baseline = 190;
    const line = points.map((point) => `L${point.x.toFixed(2)} ${point.y.toFixed(2)}`).join(' ');
    return `M${points[0].x.toFixed(2)} ${baseline} ${line} L${points[points.length - 1].x.toFixed(2)} ${baseline} Z`;
  }

  private chartY(value: number, min: number, max: number): number {
    const top = 20;
    const bottom = 190;
    const range = Math.max(1, max - min);
    return bottom - (((value - min) / range) * (bottom - top));
  }

  private blockStartLabel(block: number): string {
    const minutes = (block - 1) * 15;
    const hour = Math.floor(minutes / 60);
    const minute = minutes % 60;
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  }

  private resolveCurrentBlock(): number {
    const now = new Date();
    const minutes = now.getHours() * 60 + now.getMinutes();
    return Math.min(96, Math.max(1, Math.floor(minutes / 15) + 1));
  }

  private toFiniteNumber(value: string | number | null | undefined): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
}
